import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';

/**
 * Email chip multi-select shared by the create card, the members drawer,
 * the Regroup drawer and the group layout. Pure controlled component: the
 * caller owns the Set.
 *
 * `disabledReasons` (Samuel, 2026-09-21, after the hidden-chip debugging
 * round): a member who CANNOT be picked renders as a visible, non-clickable
 * chip whose hover says why — never silently omitted. Silent omission reads
 * as a bug every time ("where did my third member go?").
 */
export function MemberPicker({
  emails,
  selected,
  onToggle,
  disabledReasons,
}: {
  emails: string[];
  selected: Set<string>;
  onToggle: (email: string) => void;
  /** email -> why this chip can't be picked (rendered disabled + title). */
  disabledReasons?: ReadonlyMap<string, string>;
}) {
  if (emails.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
        No users on this site — add them on the Workforce page first.
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        maxHeight: 160,
        overflowY: 'auto',
      }}
    >
      {emails.map((email) => {
        const reason = disabledReasons?.get(email);
        const on = reason === undefined && selected.has(email);
        return (
          <button
            key={email}
            type="button"
            // Not the native `disabled` attribute: Firefox suppresses the
            // hover title on disabled elements, and the title IS the point.
            aria-disabled={reason !== undefined}
            title={reason}
            onClick={() => {
              if (reason === undefined) onToggle(email);
            }}
            style={{
              padding: '3px 10px',
              borderRadius: 12,
              fontSize: 12,
              cursor: reason !== undefined ? 'not-allowed' : 'pointer',
              border: `1px solid ${on ? 'var(--amber, var(--good))' : 'var(--border)'}`,
              background: on ? 'var(--amber-soft, var(--panel-2))' : 'transparent',
              color: reason !== undefined ? 'var(--muted)' : 'var(--text)',
              opacity: reason !== undefined ? 0.55 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {on ? <Icon of={Icons.status.success} size={12} /> : null}
            {email}
          </button>
        );
      })}
    </div>
  );
}
